/**
 * The batch page — where a send lands, and where the Sent list links to.
 *
 * A batch drains in the background at roughly 120 envelopes a minute, so this
 * polls while it is `Queued` or `Running` and stops once it settles. There are
 * no websockets anywhere in this app; interval polling is the house pattern.
 *
 * Cancel only stops what has not gone out. There is no batch void: a request
 * that already reached its signers is voided on its own, like any other.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Skeleton, Tooltip, Typography } from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";
import { useHistory, useParams } from "react-router-dom";
import EmptyState from "global/components/EmptyState/EmptyState";
import { centeredPageGutterSx } from "layouts/CenteredAuthenticated/CenteredPage.tokens";
import viewSignRequestStyles from "screens/MySignRequests/ViewRequest/ViewSignRequest.styles";
import viewSignRequestTokens from "screens/MySignRequests/ViewRequest/ViewSignRequest.tokens";
import {
  ArrowLeftGlyph,
  ChevronRightGlyph,
} from "screens/MySignRequests/ViewRequest/ViewRequestIcons";
import { BATCHES_TAB } from "screens/MySignRequests/components/SignRequestsFilterTabs";
import title from "global/constants/TitleConstants";
import viewpaths from "global/constants/ViewPathConstants";
import { useTitle } from "utils/UseTitle";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { hasWorkspacePermission } from "layouts/Config";
import { batchStyles } from "./BulkSend.styles";
import tokens from "./BulkSend.tokens";
import {
  BulkBatch,
  BulkBatchRow,
  BulkRowStatus,
} from "./BulkSendTypesAndValidation";
import { downloadBlob, requestCountLabel } from "./BulkSendHelper";
import {
  cancelBulkBatch,
  exportBulkBatch,
  fetchBulkBatch,
  fetchBulkBatchRows,
  retryBulkBatch,
} from "./BulkSendService";
import BatchProgress from "./components/BatchProgress";
import BatchRowsTable, { RowFilter } from "./components/BatchRowsTable";
import { RetryIcon } from "./components/BulkSendIcons";

const p = tokens.palette;
const t = tokens.batch;
const s = batchStyles;
/** The request page's own breadcrumb, borrowed whole. */
const v = viewSignRequestStyles;

/** How often to re-read a batch that is still draining. */
const POLL_MS = 4000;

const LIVE_STATUSES = ["Queued", "Running"];

const statusTone = (
  status?: string,
): "neutral" | "progress" | "success" | "danger" => {
  if (status === "Completed") return "success";
  if (status === "CompletedWithErrors") return "danger";
  if (status === "Queued" || status === "Running") return "progress";
  return "neutral";
};

const statusLabel = (status?: string): string => {
  if (status === "CompletedWithErrors") return "Completed with errors";
  if (status === "Running") return "Sending";
  return status || "Draft";
};

const statusDotColor = (status?: string): string => {
  const tone = statusTone(status);
  if (tone === "success") return p.success;
  if (tone === "danger") return p.danger;
  if (tone === "progress") return p.amber;
  return p.muted;
};

const BulkBatchStatus = () => {
  useTitle(title.BULK_BATCH);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();

  const [batch, setBatch] = useState<BulkBatch | null>(null);
  const [rows, setRows] = useState<BulkBatchRow[]>([]);
  const [filter, setFilter] = useState<RowFilter>("Failed");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingRows, setIsLoadingRows] = useState<boolean>(true);
  const [hasFailedToLoad, setHasFailedToLoad] = useState<boolean>(false);
  const [isActing, setIsActing] = useState<boolean>(false);

  const canManage = useMemo(() => hasWorkspacePermission("document.send"), []);

  /** Kept in a ref so the poll can read it without restarting the interval. */
  const filterRef = useRef<RowFilter>(filter);
  filterRef.current = filter;

  const loadRows = useCallback(
    async (nextFilter: RowFilter, showSkeleton: boolean) => {
      if (!id) return;
      try {
        if (showSkeleton) setIsLoadingRows(true);
        const response = await fetchBulkBatchRows(
          id,
          1,
          nextFilter === "All" ? undefined : (nextFilter as BulkRowStatus),
        );
        setRows(response);
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        if (showSkeleton) setIsLoadingRows(false);
      }
    },
    [id],
  );

  const loadBatch = useCallback(
    async (showSkeleton: boolean) => {
      if (!id) return null;
      try {
        if (showSkeleton) setIsLoading(true);
        const response = await fetchBulkBatch(id);
        setBatch(response);
        setHasFailedToLoad(false);
        return response;
      } catch (error: any) {
        if (showSkeleton) {
          setHasFailedToLoad(true);
          handleErrorMessage(error);
        }
        return null;
      } finally {
        if (showSkeleton) setIsLoading(false);
      }
    },
    [id],
  );

  useEffect(() => {
    loadBatch(true);
    loadRows(filterRef.current, true);
  }, [loadBatch, loadRows]);

  /** Poll only while the batch is actually draining. */
  useEffect(() => {
    if (!batch || !LIVE_STATUSES.includes(batch.status)) return undefined;

    const timer = setInterval(async () => {
      const next = await loadBatch(false);
      await loadRows(filterRef.current, false);
      if (next && !LIVE_STATUSES.includes(next.status)) {
        clearInterval(timer);
      }
    }, POLL_MS);

    return () => clearInterval(timer);
  }, [batch, loadBatch, loadRows]);

  const handleFilter = (next: RowFilter) => {
    setFilter(next);
    loadRows(next, true);
  };

  const runAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    try {
      setIsActing(true);
      await action();
      openSuccessNotification(successMessage);
      await loadBatch(false);
      await loadRows(filterRef.current, true);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActing(false);
    }
  };

  const handleExport = async () => {
    if (!id) return;
    try {
      setIsActing(true);
      const blob = await exportBulkBatch(id);
      const safeName = (batch?.name || "batch")
        .replace(/[^a-z0-9]+/gi, "-")
        .toLowerCase();
      downloadBlob(blob, `${safeName}-results.csv`);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsActing(false);
    }
  };

  const counts = batch?.statusCounts ?? {};
  const failedCount = counts.Failed ?? 0;
  const isLive = Boolean(batch && LIVE_STATUSES.includes(batch.status));
  const isSettled = Boolean(
    batch &&
    ["Completed", "CompletedWithErrors", "Cancelled"].includes(batch.status),
  );

  if (isLoading) {
    return (
      <Box sx={centeredPageGutterSx}>
        <Box sx={s.card}>
          <Box sx={s.header}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton
                variant="text"
                sx={[s.title, { width: "42%" }] as SxProps<Theme>}
              />
              <Skeleton
                variant="text"
                sx={[s.sub, { width: "28%" }] as SxProps<Theme>}
              />
            </Box>
          </Box>
          <Box sx={s.body}>
            <Skeleton variant="rectangular" sx={s.bar} />
            <Skeleton variant="text" sx={{ width: "46%" }} />
          </Box>
        </Box>
      </Box>
    );
  }

  if (hasFailedToLoad || !batch) {
    return (
      <Box sx={centeredPageGutterSx}>
        <EmptyState
          size="full"
          icon="activity"
          title="Couldn't load this batch"
          body="The request didn't come back. Try again in a moment."
          primaryAction={{
            label: "Try again",
            onClick: () => {
              loadBatch(true);
              loadRows(filterRef.current, true);
            },
          }}
          secondaryAction={{
            label: "Back to sent",
            onClick: () => history.push(viewpaths.sentSignRequestsViewPath),
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={centeredPageGutterSx}>
      {/*
        The same breadcrumb the request page uses, and for the same reason:
        this page is only ever arrived at from a list, so it has to offer the
        way back — to the Batches tab it was opened from, not to the default.
      */}
      <Box sx={v.breadcrumb}>
        <Button
          disableRipple
          sx={v.breadcrumbLink}
          onClick={() =>
            history.push(viewpaths.sentSignRequestsViewPath, {
              tab: BATCHES_TAB,
            })
          }
        >
          <ArrowLeftGlyph
            sx={{
              width: viewSignRequestTokens.breadcrumb.backIconSize,
              height: viewSignRequestTokens.breadcrumb.backIconSize,
            }}
          />
          Batches
        </Button>
        <ChevronRightGlyph sx={v.breadcrumbSeparator} />
        <Tooltip title={batch.name} arrow describeChild>
          <Typography
            component="span"
            aria-current="page"
            sx={v.breadcrumbCurrent}
          >
            {batch.name}
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={s.card}>
        <Box sx={s.header}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="span" sx={s.title}>
              {batch.name}
            </Typography>
            <Typography component="span" sx={s.sub}>
              {requestCountLabel(batch.totalRows)}
              {batch.createdBy ? ` · sent by ${batch.createdBy}` : ""}
            </Typography>
          </Box>
          <Box sx={s.statusPill(statusTone(batch.status))} role="status">
            <Box sx={s.statusDot(statusDotColor(batch.status))} />
            {statusLabel(batch.status)}
          </Box>
        </Box>

        <BatchProgress
          statusCounts={batch.statusCounts}
          totalRows={batch.totalRows}
        />

        <Box sx={s.footer}>
          <Box component="span" sx={s.footerNote}>
            {isLive
              ? "Requests go out as they are queued — this updates itself."
              : failedCount > 0
                ? `${failedCount} ${failedCount === 1 ? "recipient" : "recipients"} never received the request.`
                : "Every recipient in this batch was sent their copy."}
          </Box>

          {/*
            A fixed signer on this batch receives one request per recipient.
            The queue is how they work through those in one sitting, so it is
            offered as soon as the batch has started producing envelopes.
          */}
          {(counts.Sent ?? 0) > 0 && (
            <Button
              variant="outlined"
              onClick={() =>
                history.push(
                  `${viewpaths.bulkBatchViewPath}/${batch.id}/${viewpaths.bulkQueueSegment}`,
                )
              }
              sx={s.actionButton}
            >
              Your signing queue
            </Button>
          )}

          <Button
            variant="outlined"
            onClick={handleExport}
            disabled={isActing}
            sx={s.actionButton}
          >
            Export CSV
          </Button>

          {canManage && isLive && (
            <Button
              variant="outlined"
              onClick={() =>
                runAction(
                  () => cancelBulkBatch(batch.id),
                  "Stopped the requests that hadn't gone out.",
                )
              }
              disabled={isActing}
              sx={s.actionButton}
            >
              Cancel sending
            </Button>
          )}


          {canManage && isSettled && failedCount > 0 && (
            <Button
              variant="contained"
              startIcon={<RetryIcon size={13} />}
              onClick={() =>
                runAction(
                  () => retryBulkBatch(batch.id),
                  `Requeued ${failedCount} failed ${failedCount === 1 ? "row" : "rows"}.`,
                )
              }
              disabled={isActing}
              sx={s.actionButton}
            >
              Retry failed
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ ...s.card, mt: t.headerGap }}>
        <BatchRowsTable
          rows={rows}
          isLoading={isLoadingRows}
          filter={filter}
          counts={counts}
          totalRows={batch.totalRows}
          onFilter={handleFilter}
        />
      </Box>

    </Box>
  );
};

export default BulkBatchStatus;
